/** AIScorePanel's styles, split out of AIScorePanel.jsx to keep it under 500 lines. */
import styled from '@emotion/styled';

// --- Layout ---

export const Container = styled.div`
  width: 100%;
`;

export const TabRow = styled.div`
  display: flex;
  gap: 2px;
`;

export const Tab = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 10px;
  background: ${props => props.$active
    ? 'rgba(91, 157, 217, 0.12)'
    : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid ${props => props.$active
    ? 'rgba(91, 157, 217, 0.25)'
    : 'rgba(255, 255, 255, 0.08)'};
  border-bottom: ${props => props.$active ? 'none' : '1px solid rgba(255, 255, 255, 0.08)'};
  border-radius: 4px 4px 0 0;
  color: ${props => props.$active
    ? 'rgba(255, 255, 255, 0.85)'
    : 'rgba(255, 255, 255, 0.45)'};
  font-size: 0.72rem;
  font-family: 'Rational', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.7);
    background: ${props => props.$active
      ? 'rgba(91, 157, 217, 0.12)'
      : 'rgba(255, 255, 255, 0.06)'};
  }

  svg.chevron {
    width: 11px;
    height: 11px;
    transition: transform 0.2s ease;
    transform: rotate(${props => props.$active ? '180deg' : '0deg'});
    flex-shrink: 0;
  }
`;

export const BarInline = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.08);
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
`;

export const BarFillInline = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
  ${props => props.$bipolar ? `
    left: ${props.$value < 0 ? `${50 + props.$value / 2}%` : '50%'};
    width: ${Math.abs(props.$value) / 2}%;
    background: ${props.$value < 0 ? '#E94B6E' : '#4CAF50'};
  ` : `
    left: 0;
    width: ${props.$value}%;
    background: ${props.$value >= 70 ? '#4CAF50' : props.$value >= 40 ? '#81C784' : '#E94B6E'};
  `}
`;

export const BarCenterLine = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  background: rgba(255, 255, 255, 0.2);
`;

export const ValueBadge = styled.span`
  font-weight: 600;
  font-size: 0.65rem;
  color: ${props => props.$color};
  min-width: 18px;
  text-align: right;
`;

export const ContentArea = styled.div`
  background: rgba(91, 157, 217, 0.06);
  border: 1px solid rgba(91, 157, 217, 0.15);
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 12px 14px;
`;

export const ReasonText = styled.div`
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
`;

export const RefItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 0.78rem;
  color: ${props => props.$expanded ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.6)'};
  line-height: 1.4;
  cursor: pointer;
  padding: 3px 4px;
  margin: -3px -4px;
  border-radius: 3px;
  transition: background 0.1s ease;
  background: ${props => props.$expanded ? 'rgba(255, 255, 255, 0.04)' : 'transparent'};
  &:hover {
    color: rgba(255, 255, 255, 0.85);
    background: rgba(255, 255, 255, 0.04);
  }
`;

export const RefNumber = styled.span`
  color: ${props => props.$analysis ? '#FFB74D' : '#5B9DD9'};
  font-weight: 600;
  flex-shrink: 0;
  font-size: 0.72rem;
`;

export const RefDivider = styled.div`
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 6px 0 2px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin-top: 4px;
`;

export const RefCitation = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RefList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

/** No research could be used (#1421): a quiet line, never an error box. */
export const ResearchNote = styled.div`
  margin-top: 8px;
  font-size: 0.78rem;
  font-style: italic;
  line-height: 1.45;
  color: ${(p) => (p.$light ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.55)')};
`;

// --- Full document modal ---

export const DocBox = styled.div`
  background: #0d0d0d;
  border: 1px solid rgba(91, 157, 217, 0.3);
  border-radius: 8px;
  max-width: 720px;
  width: 94vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  color: #fff;
`;

export const DocHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
`;

export const DocTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #5B9DD9;
  line-height: 1.4;
`;

export const DocClose = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 2px;
  flex-shrink: 0;
  &:hover { color: #fff; }
  svg { width: 18px; height: 18px; }
`;

export const DocBody = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 0;
`;

export const DocChunk = styled.div`
  font-size: 0.82rem;
  line-height: 1.65;
  padding: 12px 20px;
  color: ${props => props.$isTarget ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.5)'};
  background: ${props => props.$isTarget ? 'rgba(91, 157, 217, 0.1)' : 'transparent'};
  border-left: 3px solid ${props => props.$isTarget ? '#5B9DD9' : 'transparent'};
  ${props => props.$sectionBreak ? `border-top: 1px solid rgba(255, 255, 255, 0.06);` : ''}

  h1, h2, h3, h4 {
    color: rgba(255, 255, 255, 0.85);
    margin: 0.8em 0 0.3em;
    font-size: 1em;
    &:first-child { margin-top: 0; }
  }
  h1 { font-size: 1.15em; }
  h2 { font-size: 1.05em; }
  p { margin: 0.4em 0; }
  ul, ol { margin: 0.3em 0; padding-left: 1.4em; }
  li { margin: 0.15em 0; }
  blockquote {
    border-left: 2px solid rgba(255,255,255,0.15);
    margin: 0.4em 0; padding: 0.2em 0.8em;
    color: rgba(255,255,255,0.45);
  }
  strong { color: rgba(255,255,255,0.8); }
  table { border-collapse: collapse; margin: 0.5em 0; width: 100%; font-size: 0.9em; }
  th, td { border: 1px solid rgba(255,255,255,0.1); padding: 4px 8px; text-align: left; }
  th { background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.7); }
`;

export const DocSectionHeading = styled.div`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.25);
  padding: 12px 20px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

export const DocLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.85rem;
  padding: 40px 20px;
  svg { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;
